import { huge, maxHuge, type HugeNumber } from "@/shared/numbers";
import { resolveNHovBranchEffectiveLevel } from "./nHovBranchFormulaMath";
import type { NHovHgDiscountBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export const NHOV_HG_COST_DIVISOR_AT_LEVEL_ZERO = 1;
export const NHOV_HG_COST_DIVISOR_FACTOR_POW_BASE = 2;
export const NHOV_HG_COST_DIVISOR_FACTOR_PER_LEVEL = 10;
export const NHOV_HG_DISCOUNT_OUTER_REEXECUTION_BOUNDARY: HugeNumber =
  huge("1e309");
// Linear in the shared post-knee effect level; unlike the other branches,
// hgDiscount does not apply an additional branch-specific exponent.
export const NHOV_HG_DISCOUNT_EFFECTIVE_LEVEL_EXPONENT = 1;

function isFiniteNonNegative(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

// HugeNumber-preserving core so the divisor grows past the JS number limit
// once the `outside-nhov` cap is lifted.
export function calculateNHovHgCostDivisorHuge(
  level: number,
  balance?: NHovHgDiscountBalance,
): HugeNumber {
  if (!isFiniteNonNegative(level) || level <= 0) {
    return huge(NHOV_HG_COST_DIVISOR_AT_LEVEL_ZERO);
  }
  const effectiveLevel = resolveNHovBranchEffectiveLevel({
    level,
    exponent:
      balance?.effectiveLevelExponent ??
      NHOV_HG_DISCOUNT_EFFECTIVE_LEVEL_EXPONENT,
  });
  const power =
    effectiveLevel +
    (balance?.divisorPowerOffset ?? NHOV_HG_COST_DIVISOR_FACTOR_POW_BASE);
  const factorPerLevel =
    balance?.factorPerLevel ?? NHOV_HG_COST_DIVISOR_FACTOR_PER_LEVEL;
  const base =
    factorPerLevel === 10 && Number.isInteger(power)
      ? huge(`1e${power}`)
      : huge(factorPerLevel).pow(power);
  return maxHuge(base, NHOV_HG_COST_DIVISOR_AT_LEVEL_ZERO);
}
